import React, { useEffect, useState } from "react";
import { Form, Input, DatePicker, Button, Row, Col, Card, message, Select, Radio, Modal, Checkbox, InputNumber, Typography, Space } from 'antd';
import Table from 'components/Common/Table';
import { PlusOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import * as actions from "store/actions/AuthActions";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ConvertLeadPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [campaignForm] = Form.useForm();

    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(false);
    const [createDeal, setCreateDeal] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [matchingAccounts, setMatchingAccounts] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignModalVisible, setCampaignModalVisible] = useState(false);
    const [accountSelectModalVisible, setAccountSelectModalVisible] = useState(false);
    const [contactSelectModalVisible, setContactSelectModalVisible] = useState(false);

    // Account options: 'new' or 'existing'
    const [accountOption, setAccountOption] = useState('new');
    const [selectedExistingAccount, setSelectedExistingAccount] = useState(null);
    const [selectedAccountName, setSelectedAccountName] = useState('');

    // Contact options: 'new' or 'existing'
    const [contactOption, setContactOption] = useState('new');
    const [selectedExistingContact, setSelectedExistingContact] = useState(null);
    const [selectedContactName, setSelectedContactName] = useState('');

    const [conversionSuccess, setConversionSuccess] = useState(false);
    const [conversionDetails, setConversionDetails] = useState(null);

    // Search states for modals
    const [accountSearchText, setAccountSearchText] = useState('');
    const [contactSearchText, setContactSearchText] = useState('');

    useEffect(() => {
        loadLead();
        loadEmployees();
        loadCampaigns();
        loadAccounts();
        loadContacts();
    }, [id]);

    const loadLead = async () => {
        setLoading(true);
        const response = await actions.retrieveLead(id);
        if (response) {
            setLead(response);

            // Pre-fill form with lead data
            form.setFieldsValue({
                account_name: response.company_name || response.first_name,
                contact_first_name: response.first_name || '',
                contact_last_name: response.last_name || '',
                contact_email: response.email || '',
                contact_phone: response.phone_number || '',
                deal_name: response.first_name || '',
                amount: response.converted_value || 0,
                stage: 'QUALIFICATION',
            });

            // Check for duplicate accounts
            checkForDuplicateAccounts(response.company_name || response.first_name);
        }
        setLoading(false);
    };

    const checkForDuplicateAccounts = async (accountName) => {
        if (!accountName) return;

        const response = await actions.loadAccounts(1, accountName, 100);
        if (response && response.status === 200) {
            const matches = response.data.results.filter(acc =>
                acc.name.toLowerCase() === accountName.toLowerCase()
            );
            if (matches.length > 0) {
                setMatchingAccounts(matches);
            }
        }
    };

    const loadAccounts = async () => {
        const response = await actions.loadAccounts(1, '', 1000);
        if (response && response.status === 200) {
            setAccounts(response.data.results || []);
        }
    };

    const loadContacts = async () => {
        const response = await actions.loadAllContacts('');
        if (response && response.status === 200) {
            setContacts(response.data.results || []);
        }
    };

    const loadEmployees = async () => {
        const response = await actions.loadEmployees();
        if (response && response.results) {
            setEmployees(response.results);
        }
    };

    const loadCampaigns = async () => {
        const response = await actions.loadCampaigns(1, '', '', '');
        if (response && response.status === 200) {
            setCampaigns(response.data.results || []);
        }
    };

    const handleCampaignQuickCreate = async () => {
        try {
            const values = await campaignForm.validateFields();

            const formattedValues = { ...values };
            if (formattedValues.start_date) {
                formattedValues.start_date = dayjs(formattedValues.start_date).format('YYYY-MM-DD');
            }
            if (formattedValues.end_date) {
                formattedValues.end_date = dayjs(formattedValues.end_date).format('YYYY-MM-DD');
            }

            const response = await actions.addCampaign(formattedValues);
            if (response && response.status === 201) {
                message.success('Campaign created successfully');
                setCampaignModalVisible(false);
                campaignForm.resetFields();
                form.setFieldsValue({ campaign_source: response.data.id });
                loadCampaigns();
            }
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            console.error('Campaign creation error:', error);
            message.error('Failed to create campaign');
        }
    };

    const handleConvert = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const conversionData = {};

            // Handle account selection
            if (accountOption === 'existing' && selectedExistingAccount) {
                conversionData.account_id = selectedExistingAccount;
            }
            // If 'new', backend will create new account from lead data

            // Handle contact selection
            if (contactOption === 'existing' && selectedExistingContact) {
                conversionData.contact_id = selectedExistingContact;
            }
            // If 'new', backend will create new contact from lead data

            // Only add deal data if checkbox is checked
            if (createDeal) {
                conversionData.deal_name = values.deal_name;
                conversionData.amount = values.amount;
                conversionData.closing_date = dayjs(values.closing_date).format('YYYY-MM-DD');
                conversionData.stage = values.stage;

                // Only add campaign_source if selected
                if (values.campaign_source) {
                    conversionData.campaign_source = values.campaign_source;
                }

                // Only add contact_role if selected and not empty
                if (values.contact_role && values.contact_role !== 'NONE') {
                    conversionData.contact_role = values.contact_role;
                }
            }

            if (values.employee_owner) {
                conversionData.employee_owner = values.employee_owner;
            }

            const response = await actions.convertLeadToDeal(id, conversionData);

            if (response.status === 200 || response.status === 201) {
                message.success('Lead converted successfully!');

                // Set conversion details for success view
                setConversionDetails({
                    account: response.data.account?.name || selectedAccountName || values.account_name,
                    contact: response.data.contact?.name || selectedContactName || `${values.contact_first_name} ${values.contact_last_name}`.trim(),
                    deal: createDeal ? (response.data.deal?.deal_name || values.deal_name) : null,
                });
                setConversionSuccess(true);
            } else {
                message.error(response.data?.error || 'Failed to convert lead');
            }
        } catch (error) {
            if (error.errorFields) {
                return;
            }
            console.error('Conversion error:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Failed to convert lead';
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(`/leads/edit/${id}`);
    };

    const handleGoToLeads = () => {
        navigate('/leads');
    };

    const handleAccountSelect = (account) => {
        setSelectedExistingAccount(account.id);
        setSelectedAccountName(account.name);
        setAccountOption('existing');
        setAccountSelectModalVisible(false);
    };

    const handleContactSelect = (contact) => {
        setSelectedExistingContact(contact.id);
        setSelectedContactName(`${contact.first_name || ''} ${contact.last_name || ''}`.trim());
        setContactOption('existing');
        setContactSelectModalVisible(false);
    };

    const accountColumns = [
        {
            title: 'Account Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Website',
            dataIndex: 'website',
            key: 'website',
        },
        {
            title: 'Account Type',
            dataIndex: 'account_type',
            key: 'account_type',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button type="link" onClick={() => handleAccountSelect(record)}>
                    Select
                </Button>
            ),
        },
    ];

    const contactColumns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim() || '-',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone_number',
        },
        {
            title: 'Company',
            dataIndex: 'company',
            key: 'company',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button type="link" onClick={() => handleContactSelect(record)}>
                    Select
                </Button>
            ),
        },
    ];

    // Filter accounts based on search
    const filteredAccounts = accounts.filter(acc =>
        acc.name?.toLowerCase().includes(accountSearchText.toLowerCase()) ||
        acc.phone?.toLowerCase().includes(accountSearchText.toLowerCase())
    );

    // Filter contacts based on search
    const filteredContacts = contacts.filter(contact => {
        const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
        const searchLower = contactSearchText.toLowerCase();
        return fullName.includes(searchLower) ||
            contact.email?.toLowerCase().includes(searchLower) ||
            contact.phone_number?.toLowerCase().includes(searchLower);
    });

    // Success view
    if (conversionSuccess && conversionDetails) {
        return (
            <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 30 }}>
                <Title level={3}>Convert Lead {lead && `(${lead.first_name} ${lead.last_name} - ${lead.company_name})`}</Title>

                <div style={{ marginTop: 30 }}>
                    <Text type="success" strong style={{ fontSize: 16 }}>
                        The Lead "{lead?.first_name} {lead?.last_name}" has been successfully converted.
                    </Text>

                    <div style={{ marginTop: 30 }}>
                        <Title level={5}>Conversion Details</Title>
                        <div style={{ marginTop: 15 }}>
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <Text strong>Account</Text>
                                </Col>
                                <Col span={16}>
                                    <Text style={{ color: '#1890ff' }}>{conversionDetails.account}</Text>
                                </Col>
                            </Row>
                            <Row gutter={[16, 16]}>
                                <Col span={8}>
                                    <Text strong>Contact</Text>
                                </Col>
                                <Col span={16}>
                                    <Text style={{ color: '#1890ff' }}>{conversionDetails.contact}</Text>
                                </Col>
                            </Row>
                            {conversionDetails.deal && (
                                <Row gutter={[16, 16]}>
                                    <Col span={8}>
                                        <Text strong>Deal</Text>
                                    </Col>
                                    <Col span={16}>
                                        <Text style={{ color: '#1890ff' }}>{conversionDetails.deal}</Text>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    </div>

                    <div style={{ marginTop: 30 }}>
                        <Button type="primary" onClick={handleGoToLeads}>
                            Go to Leads
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 30 }}>
            <Title level={3}>Convert Lead {lead && `(${lead.first_name} ${lead.last_name} - ${lead.company_name})`}</Title>

            {/* Account Selection Section */}
            <Card title="Account" style={{ marginTop: 20 }}>
                {matchingAccounts.length > 0 && (
                    <div style={{ marginBottom: 15, padding: 10, backgroundColor: '#fffbe6', borderRadius: 4 }}>
                        <Text type="warning">
                            Note: {matchingAccounts.length} account(s) with similar name already exist.
                        </Text>
                    </div>
                )}

                <Radio.Group
                    value={accountOption}
                    onChange={(e) => {
                        setAccountOption(e.target.value);
                        if (e.target.value === 'new') {
                            setSelectedExistingAccount(null);
                            setSelectedAccountName('');
                        }
                    }}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Radio value="new">
                            <Space>
                                <span>Create New Account:</span>
                                <Text code>{form.getFieldValue('account_name')}</Text>
                            </Space>
                        </Radio>
                        <Radio value="existing">
                            <Space>
                                <span>Attach to Existing Account</span>
                                {accountOption === 'existing' && (
                                    <>
                                        {selectedAccountName ? (
                                            <Text code>{selectedAccountName}</Text>
                                        ) : null}
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<SearchOutlined />}
                                            onClick={() => setAccountSelectModalVisible(true)}
                                        >
                                            {selectedAccountName ? 'Change' : 'Select Account'}
                                        </Button>
                                    </>
                                )}
                            </Space>
                        </Radio>
                    </Space>
                </Radio.Group>
            </Card>

            {/* Contact Selection Section */}
            <Card title="Contact" style={{ marginTop: 20 }}>
                <Radio.Group
                    value={contactOption}
                    onChange={(e) => {
                        setContactOption(e.target.value);
                        if (e.target.value === 'new') {
                            setSelectedExistingContact(null);
                            setSelectedContactName('');
                        }
                    }}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Radio value="new">
                            <Space>
                                <span>Create New Contact:</span>
                                <Text code>
                                    {`${form.getFieldValue('contact_first_name') || ''} ${form.getFieldValue('contact_last_name') || ''}`.trim() || lead?.first_name + ' ' + lead?.last_name}
                                </Text>
                            </Space>
                        </Radio>
                        <Radio value="existing">
                            <Space>
                                <span>Attach to Existing Contact</span>
                                {contactOption === 'existing' && (
                                    <>
                                        {selectedContactName ? (
                                            <Text code>{selectedContactName}</Text>
                                        ) : null}
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<SearchOutlined />}
                                            onClick={() => setContactSelectModalVisible(true)}
                                        >
                                            {selectedContactName ? 'Change' : 'Select Contact'}
                                        </Button>
                                    </>
                                )}
                            </Space>
                        </Radio>
                    </Space>
                </Radio.Group>
            </Card>

            <Form
                form={form}
                layout="vertical"
                style={{ marginTop: 30 }}
            >
                {/* Deal Creation Checkbox */}
                <Checkbox
                    checked={createDeal}
                    onChange={(e) => setCreateDeal(e.target.checked)}
                    style={{ marginBottom: 20 }}
                >
                    <Text strong>Create a new Deal for this Account.</Text>
                </Checkbox>

                {/* Deal Form - Only shown when checkbox is checked */}
                {createDeal && (
                    <Card style={{ marginBottom: 20 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Amount"
                                    name="amount"
                                    rules={[{ required: createDeal, message: 'Please enter amount' }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        prefix="Rs."
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/Rs\.\s?|(,*)/g, '')}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Deal Name"
                                    name="deal_name"
                                    rules={[{ required: createDeal, message: 'Please enter deal name' }]}
                                >
                                    <Input placeholder="Enter deal name" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Closing Date"
                                    name="closing_date"
                                    rules={[{ required: createDeal, message: 'Please select closing date' }]}
                                >
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Stage"
                                    name="stage"
                                    rules={[{ required: createDeal, message: 'Please select stage' }]}
                                >
                                    <Select placeholder="Select stage">
                                        <Select.Option value="QUALIFICATION">Qualification</Select.Option>
                                        <Select.Option value="NEEDS_ANALYSIS">Needs Analysis</Select.Option>
                                        <Select.Option value="VALUE_PROPOSITION">Value Proposition</Select.Option>
                                        <Select.Option value="IDENTIFY_DECISION_MAKERS">Identify Decision Makers</Select.Option>
                                        <Select.Option value="PROPOSAL_PRICE_QUOTE">Proposal/Price Quote</Select.Option>
                                        <Select.Option value="NEGOTIATION_REVIEW">Negotiation/Review</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Campaign Source"
                                    name="campaign_source"
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select campaign or create new"
                                        allowClear
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                        dropdownRender={(menu) => (
                                            <>
                                                {menu}
                                                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                                                    <Button
                                                        type="link"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => setCampaignModalVisible(true)}
                                                        style={{ width: '100%', textAlign: 'left' }}
                                                    >
                                                        New Campaign
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    >
                                        {campaigns.map(campaign => (
                                            <Select.Option key={campaign.id} value={campaign.id}>
                                                {campaign.campaign_name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Contact Role"
                                    name="contact_role"
                                >
                                    <Select placeholder="Select contact role (optional)" allowClear>
                                        <Select.Option value="DEVELOPER_EVALUATOR">Developer / Evaluator</Select.Option>
                                        <Select.Option value="DECISION_MAKER">Decision Maker</Select.Option>
                                        <Select.Option value="PURCHASING">Purchasing</Select.Option>
                                        <Select.Option value="EXECUTIVE_SPONSOR">Executive Sponsor</Select.Option>
                                        <Select.Option value="ENGINEERING_LEAD">Engineering Lead</Select.Option>
                                        <Select.Option value="ECONOMIC_DECISION_MAKER">Economic Decision Maker</Select.Option>
                                        <Select.Option value="PRODUCT_MANAGEMENT">Product Management</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* Owner Field */}
                <div style={{ marginTop: 20 }}>
                    <Text strong>Owner of the New Records</Text>
                    <Form.Item
                        name="employee_owner"
                        style={{ marginTop: 10 }}
                    >
                        <Select
                            showSearch
                            placeholder="Select owner (defaults to you)"
                            allowClear
                            suffixIcon={<UserOutlined />}
                            filterOption={(input, option) =>
                                (option?.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            style={{ width: 300 }}
                            options={employees.map(employee => {
                                // Clean up user_name by removing "None" and extra spaces
                                const userName = employee.user_name || '';
                                const cleanName = userName.split(' ')
                                    .filter(part => part && part !== 'None')
                                    .join(' ')
                                    .trim();

                                return {
                                    value: employee.id,
                                    label: `${cleanName || 'Unknown'} (${employee.employee_id || 'N/A'})`
                                };
                            })}
                        />
                    </Form.Item>
                </div>

                {/* Action Buttons */}
                <div style={{ marginTop: 30 }}>
                    <Space>
                        <Button
                            type="primary"
                            onClick={handleConvert}
                            loading={loading}
                        >
                            Convert
                        </Button>
                        <Button onClick={handleCancel}>
                            Cancel
                        </Button>
                    </Space>
                </div>
            </Form>

            {/* Account Selection Modal */}
            <Modal
                title="Select Existing Account"
                open={accountSelectModalVisible}
                onCancel={() => {
                    setAccountSelectModalVisible(false);
                    setAccountSearchText('');
                }}
                footer={null}
                width={900}
            >
                <Input
                    placeholder="Search accounts by name or phone..."
                    prefix={<SearchOutlined />}
                    value={accountSearchText}
                    onChange={(e) => setAccountSearchText(e.target.value)}
                    style={{ marginBottom: 16 }}
                    allowClear
                />
                <Table
                    dataSource={filteredAccounts}
                    columns={accountColumns}
                    rowKey="id"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                    size="small"
                />
            </Modal>

            {/* Contact Selection Modal */}
            <Modal
                title="Select Existing Contact"
                open={contactSelectModalVisible}
                onCancel={() => {
                    setContactSelectModalVisible(false);
                    setContactSearchText('');
                }}
                footer={null}
                width={900}
            >
                <Input
                    placeholder="Search contacts by name, email, or phone..."
                    prefix={<SearchOutlined />}
                    value={contactSearchText}
                    onChange={(e) => setContactSearchText(e.target.value)}
                    style={{ marginBottom: 16 }}
                    allowClear
                />
                <Table
                    dataSource={filteredContacts}
                    columns={contactColumns}
                    rowKey="id"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                    size="small"
                />
            </Modal>

            {/* Campaign Quick Create Modal */}
            <Modal
                title="Create New Campaign"
                open={campaignModalVisible}
                onOk={handleCampaignQuickCreate}
                onCancel={() => {
                    setCampaignModalVisible(false);
                    campaignForm.resetFields();
                }}
                okText="Save and Associate"
                cancelText="Cancel"
                width={600}
            >
                <Form
                    form={campaignForm}
                    layout="vertical"
                >
                    <Form.Item
                        label="Campaign Name"
                        name="campaign_name"
                        rules={[{ required: true, message: 'Please enter campaign name' }]}
                    >
                        <Input placeholder="Enter campaign name" />
                    </Form.Item>

                    <Form.Item
                        label="Type"
                        name="type"
                        rules={[{ required: true, message: 'Please select campaign type' }]}
                    >
                        <Select placeholder="Select campaign type">
                            <Select.Option value="NONE">-None-</Select.Option>
                            <Select.Option value="CONFERENCE">Conference</Select.Option>
                            <Select.Option value="WEBINAR">Webinar</Select.Option>
                            <Select.Option value="TRADE_SHOW">Trade Show</Select.Option>
                            <Select.Option value="PUBLIC_RELATIONS">Public Relations</Select.Option>
                            <Select.Option value="PARTNERS">Partners</Select.Option>
                            <Select.Option value="REFERRAL_PROGRAM">Referral Program</Select.Option>
                            <Select.Option value="ADVERTISEMENT">Advertisement</Select.Option>
                            <Select.Option value="BANNER_ADS">Banner Ads</Select.Option>
                            <Select.Option value="DIRECT_MAIL">Direct mail</Select.Option>
                            <Select.Option value="EMAIL">Email</Select.Option>
                            <Select.Option value="TELEMARKETING">Telemarketing</Select.Option>
                            <Select.Option value="OTHERS">Others</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Status"
                        name="status"
                    >
                        <Select placeholder="Select status">
                            <Select.Option value="NONE">-None-</Select.Option>
                            <Select.Option value="PLANNING">Planning</Select.Option>
                            <Select.Option value="ACTIVE">Active</Select.Option>
                            <Select.Option value="INACTIVE">Inactive</Select.Option>
                            <Select.Option value="COMPLETE">Complete</Select.Option>
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Start Date"
                                name="start_date"
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="End Date"
                                name="end_date"
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Expected Revenue"
                        name="expected_revenue"
                    >
                        <InputNumber
                            prefix="Rs."
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                            placeholder="Enter expected revenue"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ConvertLeadPage;
